import { motion } from "framer-motion";
import { TrendingUp, Lightbulb } from "lucide-react";

const ROWS = [
  { platform: "YouTube", leads: "1,240", customers: "143", revenue: "$11,297", width: 100 },
  { platform: "Facebook", leads: "820", customers: "96", revenue: "$7,584", width: 67 },
  { platform: "Podcasts", leads: "310", customers: "52", revenue: "$4,108", width: 36 },
  { platform: "TikTok", leads: "2,100", customers: "89", revenue: "$7,031", width: 62 },
  { platform: "Instagram", leads: "1,400", customers: "71", revenue: "$5,609", width: 50 },
];

export default function Revenue() {
  return (
    <section id="results" data-testid="revenue-section" className="border-t border-white/10 bg-[#0A0A0A]">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 py-24 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:px-10 lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#D4AF37]">
            Revenue Attribution &amp; AI Learning
          </p>
          <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
            Every dollar traced back to the relationship that earned it.
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-400">
            Partner → Campaign → Lead → Customer → Revenue. The chain is never broken, so you
            always know which stage, which partner and which platform actually pays.
          </p>
          <div className="mt-8 border-l-2 border-[#D4AF37] pl-6">
            <p className="flex items-start gap-3 text-sm leading-relaxed text-zinc-300">
              <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" />
              <span>
                <span className="font-semibold text-white">Do more of this: </span>
                &ldquo;Facebook Groups with 10K&ndash;50K highly active DELF audiences are generating
                2.4&times; more customers than larger groups.&rdquo;
              </span>
            </p>
          </div>
          <p className="mt-6 text-xs leading-relaxed text-zinc-500">
            Figures shown are illustrative sample data from the demonstration campaign.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          data-testid="revenue-widget"
          className="relative overflow-hidden border border-[#27272A] bg-[#050505]"
        >
          <div className="flex items-center justify-between border-b border-white/10 px-8 py-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-zinc-400">
                Campaign Performance — DELF Success Academy
              </span>
            </div>
          </div>
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-6 px-8 py-4 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 sm:grid-cols-[1fr_1fr_auto_auto_auto]">
            <span>Platform</span>
            <span className="hidden sm:block" />
            <span className="text-right">Leads</span>
            <span className="text-right">Customers</span>
            <span className="text-right">Revenue</span>
          </div>
          <ul className="divide-y divide-white/5 border-t border-white/5">
            {ROWS.map((r) => (
              <li
                key={r.platform}
                data-testid={`revenue-row-${r.platform.toLowerCase()}`}
                className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-6 px-8 py-4 sm:grid-cols-[1fr_1fr_auto_auto_auto]"
              >
                <span className="text-sm font-semibold text-white">{r.platform}</span>
                <div className="hidden h-1.5 overflow-hidden rounded-full bg-white/10 sm:block">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${r.width}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.1, delay: 0.3 }}
                    className="h-full rounded-full bg-[#D4AF37]"
                  />
                </div>
                <span className="text-right text-sm text-zinc-400">{r.leads}</span>
                <span className="text-right text-sm text-zinc-400">{r.customers}</span>
                <span className="text-right text-sm font-semibold text-[#D4AF37]">{r.revenue}</span>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between border-t border-white/10 bg-white/[0.03] px-8 py-5">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
              Total Attributed Revenue
            </span>
            <span data-testid="revenue-total" className="font-serif text-2xl font-semibold text-white">
              $35,629
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
